import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SharedAuthAccessTokenPayloadSchema } from '@libs/shared/domain/auth/access-token-payload.schema';
import { SharedAuthSignInChallenge } from '@libs/shared/domain/auth/sign-in.challenge.';
import { SharedAuthSignInResponseSchema } from '@libs/shared/domain/auth/sign-in.response.schema';
import { AnalyticsService } from '@services/analytics.service';
import { AuthApiService, SignInError, SignInResponse, SignInSuccess } from '@services/auth-api.service';
import { AuthStorage } from '@services/auth.storage';
import { DialogService } from '@services/dialog.service';
import { HttpError } from '@services/http-error';
import { ThemeService } from '@services/theme.service';
import { UrlService } from '@services/url.service';
import { UserService } from '@services/user.service';
import { UserModel } from '@shared/models/user';
import { IUser } from '@shared/models/user.model';
import { jwtDecode } from 'jwt-decode';
import { isError } from 'lodash';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

export type SignInResult = SignInResponse;
export type LoginResult = { signedIn: boolean; nextStep?: SharedAuthSignInChallenge; error?: string; meta?: SignInError['meta'] };
export type AccessTokenPayload = SharedAuthAccessTokenPayloadSchema & { exp: number };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiService = inject(AuthApiService);
  private analyticsService = inject(AnalyticsService);
  private router = inject(Router);
  private urlService = inject(UrlService);
  private dialogService = inject(DialogService);

  authStatus = signal(this.isUserAuthenticated());
  authStatusChanged = toObservable(this.authStatus);
  authIsRefreshing = new BehaviorSubject<boolean>(false);

  refreshSession(): Observable<SharedAuthSignInResponseSchema> {
    return this.apiService.refreshToken(this.getRefreshToken() ?? '', this.getCognitoRefreshToken() ?? '');
  }

  async login(username: string, password: string, challenge?: SharedAuthSignInChallenge, response?: string): Promise<LoginResult> {
    try {
      ThemeService.clearLocalStoragePreservingTheme();
      sessionStorage.clear();
      const result = await firstValueFrom(this.apiService.signIn(username, password, challenge, response));
      if (result.challenge) {
        return { signedIn: false, nextStep: result.challenge, meta: result.meta };
      }
      await this.onLogin(result);
      return { signedIn: true };
    } catch (err) {
      let error = new Error('Login has failed');
      if (err instanceof HttpErrorResponse) {
        error = new HttpError(err);
      } else if (isError(err)) {
        error = err;
      }
      return { signedIn: false, error: error.message };
    }
  }

  async onLogin(input: SignInSuccess) {
    localStorage.setItem(AuthStorage.ACCESS_TOKEN, input.accessToken);
    localStorage.setItem(AuthStorage.REFRESH_TOKEN, input.refreshToken);
    localStorage.setItem(AuthStorage.COGNITO_ACCESS_TOKEN, input.cognito.IdToken ?? '');
    localStorage.setItem(AuthStorage.COGNITO_REFRESH_TOKEN, input.cognito.RefreshToken ?? '');
    this.setRefreshTokenExpiration(input.refreshToken);
    const payload = this.decodeJwtToken<AccessTokenPayload>(input.accessToken);
    const user = payload.user as UserModel;
    localStorage.setItem(AuthStorage.USER, JSON.stringify(user));
    localStorage.setItem(AuthStorage.USER_OLD, JSON.stringify(UserService.mapUserToIUser(user)));
    const role = await firstValueFrom(this.apiService.getUserRole(user.role), { defaultValue: null });
    if (!role) {
      throw new Error(`User role with ID ${user.role} does not exist`);
    }

    this.analyticsService.trackLogin(user);
    this.authStatus.set(true);
  }

  async confirmPass(username: string, password: string): Promise<LoginResult> {
    try {
      ThemeService.clearLocalStoragePreservingTheme();
      const result = await firstValueFrom(this.apiService.signIn(username, password));
      if (result.challenge) {
        return { signedIn: false, nextStep: result.challenge };
      }
      await this.onLogin(result);
      return { signedIn: true };
    } catch (err) {
      const error = isError(err) ? err : new Error('Confirm Password has failed');
      console.error(err);
      return { signedIn: false, error: error.message };
    }
  }

  logout(reason: string, redirectToLogin = true) {
    console.log(`Logout: ${reason}`);
    this.revokeSession();
    this.authStatus.set(false);
    this.analyticsService.trackLogout();
    this.dialogService.dismissAllModals(`Logout: ${reason}`);
    this.dialogService.dismissSlideout(`Logout: ${reason}`);
    localStorage.setItem(AuthStorage.LOGOUT_INITIATED, '1');
    setTimeout(() => {
      ThemeService.clearLocalStoragePreservingTheme();
      sessionStorage.clear();
      if (redirectToLogin) this.router.navigate(['/login']);
    });
  }

  decodeJwtToken<T extends { exp: number }>(token: string): T {
    try {
      return token.length ? jwtDecode(token) : ({ exp: 0 } as T);
    } catch (error) {
      console.error(error);
      return { exp: 0 } as T;
    }
  }

  getAccessToken() {
    return localStorage.getItem(AuthStorage.ACCESS_TOKEN);
  }

  getCognitoAccessToken() {
    return localStorage.getItem(AuthStorage.COGNITO_ACCESS_TOKEN);
  }

  getCognitoRefreshToken() {
    return localStorage.getItem(AuthStorage.COGNITO_REFRESH_TOKEN);
  }

  getRefreshToken() {
    return localStorage.getItem(AuthStorage.REFRESH_TOKEN);
  }

  getRefreshTokenExpiration() {
    const exp = localStorage.getItem(AuthStorage.REFRESH_TOKEN_EXPIRATION);
    return exp?.length ? Number(exp) : null;
  }

  setRefreshTokenExpiration(refreshToken: string) {
    const payload = this.decodeJwtToken(refreshToken);
    const exp = Number(payload.exp ?? 0) * 1000; // Convert to milliseconds
    localStorage.setItem(AuthStorage.REFRESH_TOKEN_EXPIRATION, exp.toString());
  }

  isAccessTokenExpired() {
    const payload = this.decodeJwtToken(this.getAccessToken() ?? '');
    const exp = Number(payload.exp ?? 0) * 1000; // Convert to milliseconds
    return exp < Date.now();
  }

  isRefreshTokenExpired() {
    const exp = Number(localStorage.getItem(AuthStorage.REFRESH_TOKEN_EXPIRATION));
    return exp < Date.now();
  }

  isUserAuthenticated() {
    return (
      (!this.isAccessTokenExpired() || !this.isRefreshTokenExpired()) &&
      !!localStorage.getItem(AuthStorage.USER) &&
      !!localStorage.getItem(AuthStorage.ROLE)
    );
  }

  isTokenizedLogin() {
    return localStorage.getItem(AuthStorage.TOKENIZED_LOGIN) !== null;
  }

  /**
   * @deprecated
   * @see {@link UserService.getCurrent()}
   */
  getCurrentUser(): IUser {
    const userData = localStorage.getItem(AuthStorage.USER_OLD);
    if (!userData) {
      throw new Error('Getting user in non-user space!');
    }
    return JSON.parse(userData);
  }

  redirectToStartingPage() {
    const rootPage = this.urlService.isLogin() || this.urlService.isRoot();
    const redirectTo = new URL(document.location.toString()).searchParams.get('redirectTo');
    if (redirectTo && rootPage) {
      this.router.navigate([redirectTo]);
      return;
    }
    const userRole = UserService.getCurrentRole();
    if (userRole.defaultRoute && rootPage) {
      this.router.navigate([userRole.defaultRoute]);
    }
  }

  async fetchSession() {
    if (this.isRefreshTokenExpired()) {
      this.logout('RefreshTokenExpired');
      return null;
    }

    if (this.isAccessTokenExpired()) {
      if (this.authIsRefreshing.value) {
        await firstValueFrom(this.authIsRefreshing.pipe(filter(status => !status)));
      } else {
        this.authIsRefreshing.next(true);
        try {
          const result = await firstValueFrom(this.refreshSession());
          if (!result || !result.accessToken) {
            this.logout('AccessTokenExpired');
            return null;
          }
          localStorage.setItem(AuthStorage.ACCESS_TOKEN, result.accessToken);
          localStorage.setItem(AuthStorage.COGNITO_ACCESS_TOKEN, result.cognito.IdToken ?? '');
        } catch (error) {
          if (!AuthService.isSessionRejected(error)) {
            // Network or server failure - the session may still be valid, let the caller retry
            throw error;
          }
          console.warn('Session refresh rejected', error);
          this.logout('SessionRefreshRejected');
          return null;
        } finally {
          this.authIsRefreshing.next(false);
        }
      }
    }

    const accessToken = this.getAccessToken();
    const cognitoAccessToken = this.getCognitoAccessToken();

    return { accessToken, cognitoAccessToken };
  }

  /** The server rejects refresh for revoked, expired or idle sessions and invalid tokens. */
  private static isSessionRejected(error: unknown) {
    return error instanceof HttpErrorResponse && [400, 401, 403].includes(error.status);
  }

  /** Best-effort server-side revocation; logout must not wait for or depend on it. */
  private revokeSession() {
    const refreshToken = this.getRefreshToken();
    const cognitoRefreshToken = this.getCognitoRefreshToken();
    if (!refreshToken && !cognitoRefreshToken) return;
    this.apiService.signOut(refreshToken ?? '', cognitoRefreshToken ?? '').subscribe({
      error: error => console.warn('Sign-out request failed', error),
    });
  }
}
